import { Form, Input, AutoComplete } from "antd";
import React, { useEffect, useRef, useState } from "react";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import AddressSelector from "./AddressSelector";

function GeoAddress({ payload, onSendAddress }) {
  const [options, setOptions] = useState([]);
  const [error, setError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [addressDetails, setAddressDetails] = useState({
    name: "",
    city: "",
    region: "",
    country: "",
    lat: "",
    lng: "",
  });

  // 🔐 move to env & restrict by domain in production
  const GOOGLE_MAPS_API_KEY = "AIzaSyCGP3Rxih4W4ZGKtgMuQ7G4_xksovqY4w0";
  const loaded = useRef(false);

  const {
    ready,
    value: address, // <-- single source of truth for input text
    suggestions: { status, data },
    setValue: setInputValueAutocomplete,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    cache: 200,
  });

  // Load Google Maps script once
  // useEffect(() => {
  //   if (!loaded.current) {
  //     if (!document.querySelector("#google-maps")) {
  //       loadScript(
  //         `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
  //         document.querySelector("head"),
  //         "google-maps"
  //       );
  //     }
  //     loaded.current = true;
  //   }
  // }, []);
  useEffect(() => {
  if (!window.google || !window.google.maps) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }
    loaded.current = false; // script not ready yet
  } else {
    loaded.current = true;  // Google is already available
  }
}, []);


  // If you have a prefilled address coming from payload, set it once.
  useEffect(() => {
    if (payload?.address) {
      setInputValueAutocomplete(payload.address, false); // don't fetch suggestions for prefill
      setAddressDetails((prev) => ({ ...prev, name: payload.address }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload?.address]);

  // Update AntD options when Google suggestions change
  useEffect(() => {
    if (status === "OK") {
      setOptions(
        data.map((opt) => ({
          value: opt.description,
          label: opt.description, // AntD 5: safer to provide label as well
          key: opt.place_id,
        }))
      );
    } else {
      setOptions([]);
    }
  }, [status, data]);

  const handleGetAddress = (addressItem) => {
    const addressObj = {
      lat: addressItem?.lat,
      lng: addressItem?.lng,
      name: addressItem?.name,
      city: addressItem?.city,
      region: addressItem?.region,
      country: addressItem?.country,
    };
    setAddressDetails(addressObj);
    onSendAddress(addressObj);
  };

  const resetAll = () => {
    setInputValueAutocomplete("", true);
    clearSuggestions();
    setOptions([]);
    setError("");
    const cleared = { name: null, city: "", region: "", country: "", lat: "", lng: "" };
    setAddressDetails(null);
    onSendAddress(null);
  };

  // Called when user chooses one of the suggestions
  const handleSelect = async (selectedAddress) => {
    if (!selectedAddress) {
      resetAll();
      return;
    }
    // set text without triggering another suggestions fetch
    setInputValueAutocomplete(selectedAddress, false);
    clearSuggestions();
    setDropdownOpen(false); 

    try {
      const results = await getGeocode({ address: selectedAddress });
      const { lat, lng } = getLatLng(results[0]);
      const comps = results[0]?.address_components ?? [];
      const city = findArea(comps, "locality");
      const region = findArea(comps, "administrative_area_level_1");
      const country = findArea(comps, "country");

      const addressObj = { name: selectedAddress, city, region, country, lat, lng };
      setAddressDetails(addressObj);
      onSendAddress?.(addressObj);
      setError("");
    } catch (err) {
      console.error("Error fetching address details:", err);
      setError("Unable to fetch address details.");
    }
  };

  // Called on every keystroke in the input (use this instead of <Input onChange>)
//   const handleChange = (val) => {
//     // AntD may pass undefined in some edge clears; normalize to empty string
//     const nextVal = val ?? "";
//     if (nextVal.trim() === "") {
//       // Clear via backspace or ❌ — reset state and stop fetching
//       resetAll();
//       return;
//     }

//     setError("");
//     // If the Google script isn't ready yet, set the text but don't fetch
//     if (!ready) {
//       setInputValueAutocomplete(nextVal, false);
//       return;
//     }
//     // Normal flow: update value and fetch suggestions
//     setInputValueAutocomplete(nextVal); // shouldFetchData defaults to true
//   };

const handleChange = (val) => {
  const nextVal = val ?? "";
  if (nextVal.trim() === "") {
    resetAll();
    setDropdownOpen(false);
    return;
  }

  setError("");
  if (!ready) {
    setInputValueAutocomplete(nextVal, false);
    return;
  }
  setInputValueAutocomplete(nextVal);
  setDropdownOpen(true);   // 👈 show while typing
};

  const findArea = (components, type) => {
    const area = components.find((c) => c.types?.includes(type));
    return area?.long_name || "";
    // If you prefer ISO country codes: area?.short_name
  };

  const loadScript = (src, parent, id) => {
      const script = document.createElement("script");
      script.src = src;
      script.id = id;
      script.async = true;
      script.onload = () => {
        loaded.current = true;   // ✅ mark ready when script is loaded
      };
      parent.appendChild(script);
};


  return (
    <div>
      <Form.Item
        label="Institution Location"
        style={{ marginBottom: 5 }}
        rules={[{ required: true, message: "Please input your location!" }]}
      >
        <AddressSelector onAddressSelect={handleGetAddress} />

        <AutoComplete
          value={address}                 // 🔁 single source of truth
          onChange={handleChange}         // ⌨️ type → update hook + fetch
          onSelect={handleSelect}         // 🖱 pick a suggestion
          options={options}
          filterOption={false}            // 🔎 we rely on Google's results
          open={ dropdownOpen && Boolean(address) && options.length > 0} // 👀 show only when we have options
          notFoundContent={ready ? null : "Loading Google…"}
          // Optional: if dropdown gets hidden under other elements
          // dropdownStyle={{ zIndex: 2000 }}
          style={{ width: "100%" }}
          disabled={!loaded.current}      // prevent use before script tag added
        >
          <Input
            placeholder="Start typing..."
            allowClear                     // ❌ clear button (triggers onChange with "")
            onFocus={() => {
              // Re-fetch suggestions on focus when there is text
              if (address && ready) setInputValueAutocomplete(address);
            }}
          />
        </AutoComplete>

        <small className="danger">{error}</small>
      </Form.Item>

      {(addressDetails?.city || payload?.city) && (
        <Form.Item style={{ marginBottom: 5 }}>
          <Input value={addressDetails?.city || payload?.city} readOnly placeholder="City" />
        </Form.Item>
      )}
      {(addressDetails?.region || payload?.region) && (
        <Form.Item style={{ marginBottom: 5 }}>
          <Input value={addressDetails?.region || payload?.region} readOnly placeholder="Region" />
        </Form.Item>
      )}
      {(addressDetails?.country || payload?.country) && (
        <Form.Item style={{ marginBottom: 5 }}>
          <Input value={addressDetails?.country || payload?.country} readOnly placeholder="Country" />
        </Form.Item>
      )}
    </div>
  );
}

export default GeoAddress;
